import { ChangeDetectorRef, Component, OnInit } from "@angular/core"
import { StudentAssignmentService } from "../../services/student-assignment.service"
import { EduComponent } from "../../utils/eduComponent"
import { StudentAssignmentModel } from "../../models/student-assignment/studentAssignment.model"
import { QuestionSlideModel, Slide } from "../../models/student-assignment/slide.model"
import { SlideTypeEnum } from "../../models/student-assignment/slideType.enum"
import { ActivatedRoute, Router } from "@angular/router"
import { UserService } from "../../services/user.service"
import { ModalService } from "../../shared-components/modal/modal.service"
import { StudentAssignmentSubmitModalComponent } from "../../shared-components/modals/student-assignment-submit-modal/student-assignment-submit-modal.component"
import { GTMService } from "../../services/gtm.service"
import { PBSLM_TITLE } from "../../models/constants"
import { Title } from "@angular/platform-browser"
import { routeAnimations, routeTransition } from "../../utils/animations"

@Component({
    selector: "app-student-assignment",
    templateUrl: "./student-assignment.component.html",
    styleUrls: ["./student-assignment.component.scss"],
    host: { "[@routeTransition]": "true", "[@routeAnimations]": "false", style: "display: block;" },
    animations: [routeTransition, routeAnimations],
})
export class StudentAssignmentComponent extends EduComponent implements OnInit {
    public readonly START_VIEW = 1
    public readonly SLIDES_VIEW = 2
    public readonly COMPLETE_VIEW = 3
    private page
    public studentName: string = ""
    public assignmentCode
    public studentAssignment: StudentAssignmentModel = new StudentAssignmentModel()
    public selectedSlide: Slide = null
    public selectedSlideId: number = null
    public slideTypes = SlideTypeEnum
    public resultId: number
    public preview: boolean = false
    public isSubmitEnabled: boolean = true

    constructor(
        private assignmentService: StudentAssignmentService,
        private activeRoute: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private modalService: ModalService,
        private gtmService: GTMService,
        public userService: UserService,
        private titleService: Title
    ) {
        super()
        this.titleService.setTitle(PBSLM_TITLE)
    }

    public ngOnInit() {
        this.getAssignmentCode()

        this.addSub(
            this.activeRoute.queryParams.subscribe((params) => {
                this.resultId = params.result_id
                // the code params is only existent on the preview page
                if (params.code) {
                    this.assignmentCode = params.code
                    this.preview = true
                }
                this.addSub(
                    this.assignmentService
                        .getAssignmentByCode(this.assignmentCode, this.resultId, this.preview)
                        .subscribe((assignment: StudentAssignmentModel) => {
                            this.titleService.setTitle(`${assignment.title} | ${PBSLM_TITLE}`)
                            this.gtmService.resetDataLayer()
                            this.gtmService.pushPageView()
                            this.studentAssignment = assignment
                            this.resultId = this.resultId || assignment.resultId
                            this.initAssignment()
                        })
                )
            })
        )

        this.addSub(
            this.assignmentService.assignmentCompleted.subscribe((state) => {
                if (state) this.page = this.COMPLETE_VIEW
            })
        )

        this.addSub(
            this.assignmentService.getModalClosed$.subscribe((state) => {
                this.isSubmitEnabled = !state
            })
        )
    }

    public initAssignment() {
        if (this.resultId) {
            this.addSub(
                this.assignmentService.startAssignment(this.resultId).subscribe(() => {
                    this.page = this.SLIDES_VIEW
                    if (!this.studentAssignment.description) {
                        this.setSelectedSlide(this.studentAssignment.slides[0])
                    }
                })
            )
        } else {
            this.page = this.START_VIEW
        }
    }

    public goToPage(page) {
        this.page = page
    }

    public getPage() {
        return this.page
    }

    public isPage(page) {
        return page === this.page
    }

    public startLesson() {
        if (this.studentName && this.assignmentCode) {
            this.assignmentService
                .startQuickAssignment(this.studentName, this.assignmentCode, this.preview)
                .subscribe((response) => {
                    if (response.status === "started" || this.preview) {
                        this.resultId = response.id
                        this.studentAssignment.resultId = response.id
                        if (!this.studentAssignment.description) {
                            this.setSelectedSlide(this.studentAssignment.slides[0])
                        }
                        this.goToPage(this.SLIDES_VIEW)
                    }
                })
        }
    }

    public setSelectedSlide(slide: Slide) {
        if ((!slide && this.studentAssignment.description) || slide) {
            this.selectedSlide = slide
            this.selectedSlideId = slide ? slide.id : 0
        }
        this.cdr.detectChanges()
    }

    public nextSlide(alsoSubmit = false) {
        if (alsoSubmit && this.isLastSlide()) {
            this.submitAssignment()
        }
        let nextSlide
        if (this.selectedSlide) {
            nextSlide = this.studentAssignment.slides.find(
                (slide) => slide.order === this.selectedSlide.order + 1
            )
            this.setSelectedSlide(nextSlide ? nextSlide : this.selectedSlide)
        } else {
            this.setSelectedSlide(this.studentAssignment.slides[0])
        }
    }

    public previousSlide() {
        let previousSlide
        if (this.selectedSlide) {
            previousSlide = this.studentAssignment.slides.find(
                (slide) => slide.order === this.selectedSlide.order - 1
            )
            this.setSelectedSlide(previousSlide)
        }
    }

    public isLastSlide() {
        if (this.selectedSlide) {
            return (
                this.selectedSlide.order >=
                this.studentAssignment.slides[this.studentAssignment.slides.length - 1].order
            )
        }
        return null
    }

    public submitAssignment() {
        if (this.studentAssignment.isILP) {
            return false
        }
        this.isSubmitEnabled = false
        if (this.checkIfUnansweredQuestions()) {
            this.modalService.open(StudentAssignmentSubmitModalComponent, {
                windowClass: "student-assignment-modal",
                data: {
                    studentAssignment: this.studentAssignment,
                    preview: this.preview,
                },
            })
        } else {
            this.addSub(
                this.assignmentService
                    .completeAssignment(this.resultId, this.preview)
                    .subscribe((res) => {
                        // @ts-ignore
                        const googlePermissionGrantURL = res.location
                        if (googlePermissionGrantURL) {
                            window.location.href = googlePermissionGrantURL
                            return
                        }
                        // @ts-ignore
                        if (res.completed || this.preview) {
                            this.page = this.COMPLETE_VIEW
                        }
                        this.isSubmitEnabled = true
                    })
            )
        }
    }

    public resetAssignment() {
        window.location.href = window.location.origin + window.location.pathname
    }

    public isNameInvalid(): boolean {
        return !/\S/.test(this.studentName) || this.studentName.length > 150
    }

    private checkIfUnansweredQuestions() {
        let questionFound = false
        this.studentAssignment.slides.forEach((slide) => {
            if (slide instanceof QuestionSlideModel) {
                slide.questions.forEach((question) => {
                    if (!question.hasAnswer()) {
                        questionFound = true
                    }
                })
            }
        })

        return questionFound
    }

    private getAssignmentCode() {
        const urlFragments = this.router.url.split("/")
        let code = urlFragments[urlFragments.length - 1]
        if (code.includes("?")) {
            code = code.split("?")[0]
        }
        this.assignmentCode = code
    }
}
